import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Contractor } from '../../types';
import api from '../../services/api';
import ContractorProjectsSection from './sections/ContractorProjectsSection';
import ContractorInvoicesSection from './sections/ContractorInvoicesSection';
import ContractorPerformanceSection from './sections/ContractorPerformanceSection';

const ContractorDetails: React.FC = () => {
  const { contractorId } = useParams<{ contractorId: string }>();
  const navigate = useNavigate();
  const [contractor, setContractor] = useState<Contractor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContractor = useCallback(async () => {
    if (!contractorId) {
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/contractors/${contractorId}`);
      setContractor(response.data);
    } catch (error: any) {
      console.error('Error fetching contractor:', error);
      setError(error.response?.data?.message || 'Failed to load contractor details');
    } finally {
      setLoading(false);
    }
  }, [contractorId]);

  useEffect(() => {
    fetchContractor();
  }, [fetchContractor]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !contractor) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/contractors')}
          sx={{ mb: 2 }}
        >
          Back to Contractors
        </Button>
        <Alert severity="error">{error || 'Contractor not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/contractors')}
        sx={{ mb: 2 }}
      >
        Back to Contractors
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {contractor.name}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {contractor.contactEmail || contractor.user?.email || 'No email'}
            </Typography>
          </Box>
          <Chip
            label={contractor.isActive ? 'Active' : 'Inactive'}
            color={contractor.isActive ? 'success' : 'default'}
            size="medium"
          />
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <ContractorProjectsSection contractor={contractor} onUpdate={fetchContractor} />
        <Divider />
        <ContractorInvoicesSection contractor={contractor} />
        <Divider />
        <ContractorPerformanceSection contractor={contractor} />
      </Box>
    </Box>
  );
};

export default ContractorDetails;

